import { KeenIcon } from '@/components';
import ConfirmationModal from '@/components/ConfirmationModal';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  useGetPOIsQuery,
  useCreatePOIMutation,
  useUpdatePOIMutation,
  useDeletePOIMutation,
} from '@/services/POIManagementService';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { useSnackbar } from 'notistack';
import { usePermissions } from '@/hooks/usePermissions';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { UserRoles } from '@/constant';
import { useCurrentUserRoles } from '@/utils/userRole';
import { HarborCapability } from '@/constant/harbour';
import MapComponent from '../poi-management/POIMapView';
import { HarborDetailsView } from './HarborDetailsView';
import { useGetHarborByIdQuery } from '@/services/HarborManagementService';
import BookingListings from '../booking-management/BookingListings';
import { BerthListings } from '../berth-management/BerthListings';
import { BerthCategoryManagement } from '../berth-category/BerthCategoryManagement';
import { AddonListings } from '../addon-management/AddonListings';
import HarborClubRequests from './HarborClubRequests';
import HarborAccessInformation from './HarborAccessInformation';
import { IPOIData } from '@/types/poi-management.types';
import {
  Clock,
  Edit3,
  ImageIcon,
  Navigation,
  Star,
  Tag,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Lock,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/card/card';
import PoiModal from '../poi-management/POIModal';
import { PierListings } from '../group-management/PiersListings';
import { Demo1LayoutContext, useDemo1Layout } from '@/layouts/demo1';
import { useLanguage } from '@/i18n';
import AvailabilityCalendar from './AvailabilityCalendar';
import { useGetPOICategoriesQuery } from '@/services/POIManagementService';
import CategoryFilter from '../poi-management/CategoryFilter';
import { Tooltip } from 'react-tooltip';
import { ServiceRequestManagement } from '../service-request-management';
import { HarborServiceListings } from './components/HarborServiceListings';
import { PoiRequestManagement } from '../poi-request-management/PoiRequestManagement';
import ServiceBookingManagement from '../service-management/ServiceBookingManagement';
import HarborFinanceReports from './HarborFinanceReports';
import TourismHighlightsTab from './TourismHighlightsTab';
import { formatFinnishCoordinate } from '@/utils';
import { formatFinnishNumber } from '@/utils';

// Haversine formula to calculate distance in nautical miles
const normalizeCoordinate = (
  value: number | string | null | undefined
): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const parsed = Number(String(value).replace(/\s/g, '').replace(',', '.'));
  return Number.isNaN(parsed) ? null : parsed;
};

const calculateDistance = (
  lat1: number | string,
  lon1: number | string,
  lat2: number | string,
  lon2: number | string
): number => {
  const normalizedLat1 = normalizeCoordinate(lat1);
  const normalizedLon1 = normalizeCoordinate(lon1);
  const normalizedLat2 = normalizeCoordinate(lat2);
  const normalizedLon2 = normalizeCoordinate(lon2);

  if (
    normalizedLat1 === null ||
    normalizedLon1 === null ||
    normalizedLat2 === null ||
    normalizedLon2 === null
  ) {
    return 0;
  }

  const R = 3440.065; // Earth's radius in nautical miles
  const dLat = ((normalizedLat2 - normalizedLat1) * Math.PI) / 180;
  const dLon = ((normalizedLon2 - normalizedLon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((normalizedLat1 * Math.PI) / 180) *
    Math.cos((normalizedLat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const baseNavItems = [
  { name: 'Harbor Management', href: '#', id: 'harbor-management' },
  { name: 'Availability Calendar', href: '#', id: 'availability-calendar' },
  { name: 'POI Management', href: '#', id: 'poi-management' },
  { name: 'Tourism Highlights', href: '#', id: 'tourism-highlights' },
  { name: 'Piers Management', href: '#', id: 'piers-management' },
  { name: 'Berth Management', href: '#', id: 'berth-management' },
  { name: 'Berth Category', href: '#', id: 'berth-category-management' },
  {
    name: 'Access Information',
    href: '#',
    id: 'access-information',
  },
  { name: 'Addon Management', href: '#', id: 'addon-management' },
  { name: 'Service Management', href: '#', id: 'service-management' },
  { name: 'Booking Management', href: '#', id: 'booking-management' },
  {
    name: 'Service Booking Management',
    href: '#',
    id: 'service-booking-management',
  },
  {
    name: 'Service Request Management',
    href: '#',
    id: 'service-request-management',
  },
  {
    name: 'POI Request Management',
    href: '#',
    id: 'poi-request-management',
  },
  {
    name: 'Finance Reports',
    href: '#',
    id: 'finance-reports',
  },
  {
    name: 'Club Associations',
    href: '#',
    id: 'club-requests',
  },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const HarborManagement = ({ harbor }: { harbor: any }) => {
  return harbor ? <HarborDetailsView harbor={harbor} /> : null;
};

const BerthManagement = () => <BerthListings />;

const AddonManagement = () => {
  const location = useLocation();
  const harborId = (location.state as { harborId?: string })?.harborId;
  return <AddonListings harborId={harborId} />;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const POIManagement = ({ harbor }: { harbor: any }) => {
  const { currentLanguage } = useLanguage();
  const { hasPermission } = usePermissions();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedPOIs, setSelectedPOIs] = useState<any[]>([]);
  const originalPoiImageUrlsRef = useRef<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [poiToDelete, setPoiToDelete] = useState<{
    placeId: string;
    name: string;
  } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [formData, setFormData] = useState<Partial<IPOIData>>({
    status: 'Active',
    category: '',
    tags: [],
    images: [],
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [translations, setTranslations] = useState<
    Record<string, Partial<IPOIData>>
  >({});
  const [createPOI] = useCreatePOIMutation();
  const [updatePOI] = useUpdatePOIMutation();
  const [deletePOI] = useDeletePOIMutation();
  const { enqueueSnackbar } = useSnackbar();
  const [expandedPhotos, setExpandedPhotos] = useState<string | null>(null);
  const harborId = useSelector(
    (state: RootState) => state.auth.selectedHarborId
  );

  const {
    data: categoriesResponse,
    isLoading: isCategoriesLoading,
    refetch: refetchCategories,
  } = useGetPOICategoriesQuery();
  const categories = categoriesResponse || [];
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isCategoryFilterOpen, setIsCategoryFilterOpen] = useState(false);

  const selectedCategoryIds = selectedCategories.filter((id) =>
    categories.some((c) => c.id === id)
  );
  const selectedSubtypeIds = selectedCategories.filter((id) =>
    categories.some((c) => c.subtypes?.some((s) => s.id === id))
  );
  const categoryIdsParam = selectedCategoryIds.length
    ? selectedCategoryIds
    : undefined;
  const subtypeIdsParam = selectedSubtypeIds.length
    ? selectedSubtypeIds
    : undefined;
  const {
    data: poisData,
    isLoading,
    isError,
    refetch: refetchPOIs,
  } = useGetPOIsQuery(
    {
      harborId: harborId || '',
      categoryIds: categoryIdsParam,
      subtypeIds: subtypeIdsParam,
    },
    { skip: !harborId }
  );

  useEffect(() => {
    refetchCategories();
    refetchPOIs();
  }, [currentLanguage.code, refetchCategories, refetchPOIs]);

  useEffect(() => {
    if (poisData && !isLoading && !isError && harborId && harbor) {
      const initialPOIs = poisData.data.map((poi) => {
        // Parse opening_hours if it's a string
        let openingHours = poi.opening_hours;
        if (typeof poi.opening_hours === 'string' && poi.opening_hours) {
          try {
            openingHours = JSON.parse(poi.opening_hours);
          } catch (e) {
            console.warn(`Failed to parse opening_hours for POI ${poi.id}:`, e);
            openingHours = poi.opening_hours; // Fallback to original string
          }
        }
        return {
          placeId: poi.id || '',
          name: poi.name || 'Unknown',
          description: poi.description || '',
          lat: normalizeCoordinate(poi.latitude) ?? 0,
          lng: normalizeCoordinate(poi.longitude) ?? 0,
          photos: (poi.images || []).slice(0, 5),
          categoryObj:
            typeof poi.categoryObj === 'string'
              ? poi.categoryObj || ''
              : poi.categoryObj?.label ||
              poi.categoryObj?.label_en ||
              poi.categoryObj?.name ||
              '',
          category_id:
            typeof poi.categoryObj === 'object'
              ? poi.categoryObj?.id || ''
              : '',
          subtype_id:
            typeof poi.subtype === 'object'
              ? poi.subtype?.id || ''
              : poi.subtype_id || '',
          subtypeLabel:
            typeof poi.subtype === 'object'
              ? poi.subtype?.label ||
              poi.subtype?.label_en ||
              poi.subtype?.name ||
              ''
              : '',
          opening_hours: openingHours || '',
          entry_fee: poi.entry_fee ?? undefined,
          contact_info: poi.contact_info || '',
          accessibility_notes: poi.accessibility_notes || '',
          tags: poi.tags || [],
          distanceToHarbor:
            normalizeCoordinate(harbor.latitude) !== null &&
              normalizeCoordinate(harbor.longitude) !== null
              ? calculateDistance(
                harbor.latitude,
                harbor.longitude,
                normalizeCoordinate(poi.latitude) ?? 0,
                normalizeCoordinate(poi.longitude) ?? 0
              )
              : undefined,
          media_captions:
            poi.media_captions && poi.media_captions.length > 0
              ? poi.media_captions
              : poi.image_captions?.map((caption) => ({
                image_index: caption.image_index,
                captions: caption.translations,
              })) || [],
          isNew: false,
        };
      });
      setSelectedPOIs(initialPOIs);
    }
  }, [poisData, isLoading, isError, harborId, harbor]);

  const handleSelectPOI = async (
    placeId: string,
    name: string,
    description: string,
    lat: number,
    lng: number,
    photos: string[],
    address: string
  ) => {
    const harborLat = normalizeCoordinate(harbor?.latitude);
    const harborLng = normalizeCoordinate(harbor?.longitude);
    const poiLat = normalizeCoordinate(lat);
    const poiLng = normalizeCoordinate(lng);

    if (
      !harbor ||
      harborLat === null ||
      harborLng === null ||
      poiLat === null ||
      poiLng === null
    ) {
      return;
    }

    const distanceToHarbor = calculateDistance(
      harborLat,
      harborLng,
      poiLat,
      poiLng
    );
    setFormData({
      name,
      description,
      latitude: poiLat,
      longitude: poiLng,
      images: photos.slice(0, 5),
      address,
      status: 'Active',
      category: '',
      tags: [],
      distance: distanceToHarbor,
    });
    originalPoiImageUrlsRef.current = photos.slice(0, 5);
    setModalMode('create');
    setIsModalOpen(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleEditPOI = (poi: any) => {
    // Parse opening_hours if it's a string
    let openingHours = poi.opening_hours;
    if (typeof poi.opening_hours === 'string' && poi.opening_hours) {
      try {
        openingHours = JSON.parse(poi.opening_hours);
      } catch (e) {
        console.warn(
          `Failed to parse opening_hours for POI ${poi.placeId}:`,
          e
        );
        openingHours = poi.opening_hours; // Fallback to original string
      }
    }
    setFormData({
      id: poi.placeId,
      name: poi.name,
      category_id: poi.category_id,
      subtype_id: poi.subtype_id,
      description: poi.description,
      latitude: poi.lat,
      longitude: poi.lng,
      images: poi.photos || [],
      address: poi.address,
      status: 'Active',
      category: poi.category || '',
      tags: poi.tags || [],
      distance: poi.distanceToHarbor,
      opening_hours: openingHours || '',
      entry_fee: poi.entry_fee,
      contact_info: poi.contact_info || '',
      accessibility_notes: poi.accessibility_notes || '',
      media_captions: poi.media_captions || [],
    });
    originalPoiImageUrlsRef.current = (poi.photos || []).filter(
      (item: unknown): item is string => typeof item === 'string'
    );
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    originalPoiImageUrlsRef.current = [];
    setFormData({
      status: 'Active',
      category: '',
      tags: [],
      images: [],
      mediaToDelete: [],
    });
    setErrors({});
    setTranslations({});
    setModalMode('create');
    originalPoiImageUrlsRef.current = [];
  };

  const handleSubmitPOI = async () => {
    setIsSaving(true);
    try {
      const formDataToSend = new FormData();
      const normalizedLatitude = normalizeCoordinate(formData.latitude);
      const normalizedLongitude = normalizeCoordinate(formData.longitude);
      const normalizedDistance = normalizeCoordinate(formData.distance);

      formDataToSend.append('name', formData.name || '');
      formDataToSend.append('distance', normalizedDistance?.toString() || '0');
      formDataToSend.append('category_id', String(formData.category_id || ''));
      if (formData.subtype_id) {
        formDataToSend.append('subtype_id', String(formData.subtype_id));
      }
      formDataToSend.append('latitude', normalizedLatitude?.toString() || '0');
      formDataToSend.append(
        'longitude',
        normalizedLongitude?.toString() || '0'
      );
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('harbor_id', harborId || '');

      const currentMedia = (formData.images || []).slice(0, 5);
      const mediaFiles = currentMedia.filter(
        (item): item is File => item instanceof File
      );
      const mediaUrls = currentMedia.filter(
        (item): item is string => typeof item === 'string'
      );
      const mediaToDelete = (formData.mediaToDelete || []).filter(
        (item): item is string => typeof item === 'string' && Boolean(item)
      );

      if (modalMode === 'create') {
        mediaFiles.forEach((file) => {
          formDataToSend.append('mediaFiles[]', file);
        });
        mediaUrls.forEach((url) => {
          formDataToSend.append('images[]', url);
        });
      } else {
        const originalUrls = originalPoiImageUrlsRef.current || [];
        const newlyAddedUrls = mediaUrls.filter(
          (url) => !originalUrls.includes(url)
        );

        mediaFiles.forEach((file) => {
          formDataToSend.append('mediaFiles[]', file);
        });
        newlyAddedUrls.forEach((url) => {
          formDataToSend.append('images[]', url);
        });
        mediaToDelete.forEach((url) => {
          formDataToSend.append('mediaToDelete[]', url);
        });
      }

      (formData.tags || []).forEach((tag) => {
        formDataToSend.append('tags[]', tag);
      });
      if (formData.contact_info) {
        formDataToSend.append('contact_info', formData.contact_info || '');
      }
      if (formData.entry_fee) {
        formDataToSend.append('entry_fee', String(formData.entry_fee || ''));
      }
      if (formData.opening_hours) {
        // Only stringify if opening_hours is an object
        const openingHours =
          typeof formData.opening_hours === 'string'
            ? formData.opening_hours
            : JSON.stringify(formData.opening_hours);
        formDataToSend.append('opening_hours', openingHours);
      }
      if (formData.accessibility_notes) {
        formDataToSend.append(
          'accessibility_notes',
          formData.accessibility_notes || ''
        );
      }

      // Add translations if available
      if (translations && Object.keys(translations).length > 0) {
        formDataToSend.append('translations', JSON.stringify(translations));
      }

      if (formData.media_captions && formData.media_captions.length > 0) {
        formDataToSend.append(
          'image_captions',
          JSON.stringify(formData.media_captions)
        );
      }

      if (modalMode === 'create') {
        const response = await createPOI({
          data: formDataToSend,
          harborId,
        }).unwrap();
        setSelectedPOIs((prev) => [
          ...prev,
          {
            placeId: `temp-${Date.now()}`,
            name: formData.name,
            description: formData.description,
            lat: normalizedLatitude ?? 0,
            lng: normalizedLongitude ?? 0,
            photos: (formData.images || []).slice(0, 5),
            address: formData.address || 'Address not available',
            category: formData.category || '',
            tags: formData.tags || [],
            distanceToHarbor: normalizedDistance ?? undefined,
            isNew: false,
            opening_hours: formData.opening_hours || '',
            category_id: formData.category_id || '',
            subtype_id: formData.subtype_id || '',
            subtypeLabel: '',
            entry_fee: formData.entry_fee,
            contact_info: formData.contact_info || '',
            accessibility_notes: formData.accessibility_notes || '',
          },
        ]);
        enqueueSnackbar('POI created successfully', { variant: 'success' });
        if (response.warning_message) {
          enqueueSnackbar(response.warning_message, { variant: 'warning' });
        }
      } else {
        if (!formData.id) throw new Error('POI ID is required for update');
        const response = await updatePOI({
          poi_id: formData.id,
          data: formDataToSend,
          harborId,
        }).unwrap();
        setSelectedPOIs((prev) => {
          const catObj = categories.find((c) => c.id === formData.category_id);
          const catLabel =
            catObj?.label || catObj?.label_en || catObj?.name || '';
          const subObj = catObj?.subtypes?.find(
            (s) => s.id === formData.subtype_id
          );
          const subLabel =
            subObj?.label || subObj?.label_en || subObj?.name || '';
          return prev.map((poi) =>
            poi.placeId === formData.id
              ? {
                ...poi,
                name: formData.name,
                description: formData.description,
                lat: normalizedLatitude ?? 0,
                lng: normalizedLongitude ?? 0,
                photos: (formData.images || []).slice(0, 5),
                address: formData.address || 'Address not available',
                category: catLabel || poi.category || '',
                category_id: formData.category_id || poi.category_id,
                subtype_id: formData.subtype_id || '',
                subtypeLabel: subLabel || '',
                opening_hours:
                  formData.opening_hours || poi.opening_hours || '',
                entry_fee: formData.entry_fee ?? poi.entry_fee,
                contact_info: formData.contact_info || poi.contact_info || '',
                accessibility_notes:
                  formData.accessibility_notes ||
                  poi.accessibility_notes ||
                  '',
                tags: formData.tags || [],
                distanceToHarbor: normalizedDistance ?? undefined,
              }
              : poi
          );
        });
        enqueueSnackbar('POI updated successfully', { variant: 'success' });
        if (response.warning_message) {
          enqueueSnackbar(response.warning_message, { variant: 'warning' });
        }
      }
      handleModalClose();
    } catch (error) {
      console.error(
        `Error ${modalMode === 'create' ? 'creating' : 'updating'} POI:`,
        error
      );
      enqueueSnackbar(
        `Failed to ${modalMode === 'create' ? 'create' : 'update'} POI.`,
        { variant: 'error' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePOI = async (placeId: string) => {
    setPoiToDelete(null);
    setIsSaving(true);
    try {
      await deletePOI({ poi_id: placeId, harborId }).unwrap();
      setSelectedPOIs((prev) => prev.filter((p) => p.placeId !== placeId));
      enqueueSnackbar('POI deleted successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting POI:', error);
      enqueueSnackbar('Failed to delete POI.', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const savePOIs = async () => {
    setIsSaving(true);
    let successCount = 0;
    let errorCount = 0;
    let translationWarningMessage: string | undefined;

    const newPOIs = selectedPOIs.filter((poi) => poi.isNew);
    for (const poi of newPOIs) {
      const normalizedPoiLat = normalizeCoordinate(poi.lat);
      const normalizedPoiLng = normalizeCoordinate(poi.lng);
      const normalizedPoiDistance = normalizeCoordinate(poi.distanceToHarbor);
      const formData = new FormData();
      formData.append('name', poi.name);
      formData.append('distance', normalizedPoiDistance?.toString() || '0');
      formData.append('category_id', poi.category_id || '');
      if (poi.subtype_id) {
        formData.append('subtype_id', poi.subtype_id);
      }
      formData.append('latitude', normalizedPoiLat?.toString() || '0');
      formData.append('longitude', normalizedPoiLng?.toString() || '0');
      formData.append('description', poi.description);
      if (poi.opening_hours) {
        // Only stringify if opening_hours is an object
        const openingHours =
          typeof poi.opening_hours === 'string'
            ? poi.opening_hours
            : JSON.stringify(poi.opening_hours);
        formData.append('opening_hours', openingHours);
      }
      if (poi.entry_fee) {
        formData.append('entry_fee', String(poi.entry_fee));
      }
      if (poi.contact_info) {
        formData.append('contact_info', poi.contact_info);
      }
      if (poi.accessibility_notes) {
        formData.append('accessibility_notes', poi.accessibility_notes);
      }
      formData.append('harbor_id', harborId || '');
      poi.photos.slice(0, 5).forEach((photo: string | File) => {
        if (photo instanceof File) {
          formData.append('mediaFiles[]', photo);
        } else {
          formData.append('images[]', photo as string);
        }
      });

      if (poi.media_captions && poi.media_captions.length > 0) {
        formData.append('image_captions', JSON.stringify(poi.media_captions));
      }

      try {
        const response = await createPOI({ data: formData, harborId }).unwrap();
        if (response.warning_message) {
          translationWarningMessage = response.warning_message;
        }
        successCount++;
      } catch (error) {
        console.error(`Error saving POI ${poi.name}:`, error);
        errorCount++;
      }
    }

    setSelectedPOIs((prev) =>
      prev.map((poi) => (poi.isNew ? { ...poi, isNew: false } : poi))
    );

    setIsSaving(false);

    if (successCount > 0) {
      enqueueSnackbar(`${successCount} POI(s) saved successfully`, {
        variant: 'success',
      });
    }
    if (translationWarningMessage) {
      enqueueSnackbar(translationWarningMessage, {
        variant: 'warning',
      });
    }
    if (errorCount > 0) {
      enqueueSnackbar(`${errorCount} POI(s) failed to save`, {
        variant: 'error',
      });
    }
  };

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
      </div>
    );
  if (isError)
    return <div className="text-red-600 text-center">Error loading POIs.</div>;

  const hasNewPOIs = selectedPOIs.some((poi) => poi.isNew);

  const harborLocation = harbor
    ? {
      lat: normalizeCoordinate(harbor.latitude) ?? 0,
      lng: normalizeCoordinate(harbor.longitude) ?? 0,
      name: harbor.name,
    }
    : { lat: 0, lng: 0, name: '' };

  const getCategoryColor = (category: string) => {
    const colors = {
      Landmark: 'bg-amber-100 text-amber-800 border-amber-200',
      Entertainment: 'bg-purple-100 text-purple-800 border-purple-200',
      Museum: 'bg-blue-100 text-blue-800 border-blue-200',
      Restaurant: 'bg-green-100 text-green-800 border-green-200',
      Nature: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      Shopping: 'bg-pink-100 text-pink-800 border-pink-200',
      Hotel: 'bg-orange-100 text-orange-800 border-orange-200',
      Market: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    };
    return (
      colors[category as keyof typeof colors] ||
      'bg-gray-100 text-gray-800 border-gray-200'
    );
  };

  return (
    <div className="harbor-form harbor-page space-y-6 p-6 bg-gray-100 shadow-md">
      <MapComponent
        onSelectPOI={handleSelectPOI}
        selectedPOIs={selectedPOIs}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        harborLocation={harborLocation}
      />
      <div>
        <div className="poi-header form-header flex justify-between items-center my-4">
          <h1 className="text-2xl font-bold text-gray-900">
            Selected Points of Interest
          </h1>
          <div className="poi-actions form-actions flex items-center gap-3">
            <CategoryFilter
              categories={categories}
              selectedCategories={selectedCategories}
              setSelectedCategories={setSelectedCategories}
              isOpen={isCategoryFilterOpen}
              setIsOpen={setIsCategoryFilterOpen}
              isLoading={isCategoriesLoading}
            />
            {hasNewPOIs && (
              <button
                onClick={savePOIs}
                className="btn btn-save-pois px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center justify-center"
                disabled={isSaving}
              >
                {isSaving ? (
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-white mr-2"></div>
                ) : null}
                {isSaving ? 'Saving POIs...' : 'Save POIs'}
              </button>
            )}
          </div>
        </div>
        {selectedPOIs.length === 0 ? (
          <p className="text-gray-600 text-center">No POIs selected yet.</p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {selectedPOIs
              .filter((poi) => {
                if (selectedCategories.length === 0) return true;
                const cid = poi.category_id || '';
                const sid = poi.subtype_id || '';
                return (
                  (cid && selectedCategories.includes(cid)) ||
                  (sid && selectedCategories.includes(sid))
                );
              })
              .map((poi) => (
                <Card
                  key={poi.placeId}
                  className="group hover:shadow-xl transition-all duration-300 border-0 shadow-md bg-gray-200 overflow-hidden p-4 flex flex-col"
                  style={{
                    minHeight:
                      expandedPhotos === poi.placeId ? 'auto' : '500px',
                  }}
                >
                  <CardHeader className="pb-3 flex-shrink-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h2 className="text-lg font-bold text-gray-900 truncate group-hover:text-blue-600 dark:group-hover:text-blue-200 transition-colors contrast:text-white">
                          {poi.name}
                        </h2>
                      </div>
                      {poi.rating && (
                        <div className="flex items-center bg-yellow-50 px-2 py-1 rounded-full ml-2">
                          <Star className="w-3 h-3 text-yellow-500 mr-1" />
                          <span className="text-xs font-medium text-yellow-700">
                            {poi.rating}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <div
                        className={`w-fit text-xs font-semibold text-foreground inline-flex items-center rounded-full border px-2.5 py-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-gray-500 ${getCategoryColor(poi.category)}`}
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {poi.categoryObj || 'Uncategorized'}
                      </div>
                      {poi.subtypeLabel ? (
                        <span className="text-xs text-gray-600">
                          › {poi.subtypeLabel}
                        </span>
                      ) : null}
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 flex-1 flex flex-col">
                    <div className={`h-16 flex-shrink-0`}>
                      <p
                        className={`text-gray-600 text-sm leading-relaxed line-clamp-3 overflow-hidden`}
                        data-tooltip-id="descriptionTooltip"
                        data-tooltip-content={
                          poi.description || 'No description available'
                        }
                      >
                        {poi.description || 'No description available'}
                      </p>
                      <Tooltip
                        id="descriptionTooltip"
                        className="!max-w-xs z-[1000]"
                      />
                    </div>

                    <div className="h-12 flex-shrink-0">
                      {poi.tags && poi.tags.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {poi.tags.slice(0, 4).map((tag: string) => (
                            <div
                              key={tag}
                              className="bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200 border-blue-200 dark:border-blue-700 text-foreground inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold"
                            >
                              {tag}
                            </div>
                          ))}
                          {poi.tags.length > 4 && (
                            <div className="text-foreground inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-gray-500">
                              +{poi.tags.length - 4} more
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-600 dark:text-gray-700 italic contrast:text-white/90">
                          No tags available
                        </div>
                      )}
                    </div>

                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center text-gray-700">
                          <ImageIcon className="w-4 h-4 mr-1" />
                          <span className="text-sm font-medium">Photos</span>
                        </div>
                        {poi.photos && poi.photos.length > 3 && (
                          <button
                            className="text-xs h-6 px-2"
                            onClick={() =>
                              setExpandedPhotos(
                                expandedPhotos === poi.placeId
                                  ? null
                                  : poi.placeId
                              )
                            }
                          >
                            {expandedPhotos === poi.placeId
                              ? 'Show less'
                              : `+${poi.photos.length - 3} more`}
                          </button>
                        )}
                      </div>

                      {!poi.photos || poi.photos.length === 0 ? (
                        <div className="flex items-center justify-center h-20 bg-gray-100 rounded-lg border-2 border-dashed border-gray-200">
                          <div className="text-center">
                            <ImageIcon className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                            <p className="text-xs text-gray-800">
                              No photos available
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="grid grid-cols-3 gap-2"
                          style={{
                            gridTemplateRows: `repeat(${Math.ceil((expandedPhotos === poi.placeId ? poi.photos.length : Math.min(3, poi.photos.length)) / 3)}, 80px)`,
                          }}
                        >
                          {poi.photos
                            .slice(
                              0,
                              expandedPhotos === poi.placeId
                                ? poi.photos.length
                                : 3
                            )
                            .map((photo: string, index: number) => (
                              <div key={index} className="relative group/photo">
                                <img
                                  src={photo || '/placeholder.svg'}
                                  alt={`${poi.name} photo ${index + 1}`}
                                  className="w-full h-20 object-cover rounded-lg border border-gray-200 group-hover/photo:scale-105 transition-transform duration-200"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover/photo:bg-opacity-20 transition-all duration-200 rounded-lg" />
                              </div>
                            ))}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2 text-sm flex-shrink-0">
                      <div className="flex items-center justify-between text-gray-600">
                        <div className="flex items-center">
                          <Navigation className="w-4 h-4 mr-2" />
                          <span>Coordinates</span>
                        </div>
                        <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                          {formatFinnishCoordinate(poi.lat, {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                          })}{' '}
                          ,
                          {formatFinnishCoordinate(poi.lng, {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                          })}
                        </span>
                      </div>

                      {poi.distanceToHarbor !== undefined && (
                        <div className="flex items-center justify-between text-gray-600">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-2" />
                            <span>Distance to Harbor</span>
                          </div>
                          <span className="font-semibold text-blue-600 contrast:text-blue-200">
                            {formatFinnishNumber(
                              poi.distanceToHarbor.toFixed(1)
                            )}{' '}
                            nm
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex-1"></div>

                    <div className="flex gap-2 pt-2 flex-shrink-0">
                      {hasPermission('poi_management_update') && (
                        <button
                          onClick={() => handleEditPOI(poi)}
                          disabled={isSaving}
                          className="flex-1 border rounded-lg border-blue-300 dark:border-blue-900 contrast:border-blue-800 text-blue-600 dark:text-blue-200 contrast:text-blue-200 bg-blue-50 dark:bg-blue-800 contrast:bg-blue-800 px-4 py-2 flex items-center justify-center hover:bg-blue-100 dark:hover:bg-blue-900 contrast:hover:bg-blue-900 transition-colors"
                        >
                          <Edit3 className="w-4 h-4 mr-2" />
                          Edit
                        </button>
                      )}
                      {hasPermission('poi_management_delete') && (
                        <button
                          onClick={() =>
                            setPoiToDelete({
                              placeId: poi.placeId,
                              name: poi.name,
                            })
                          }
                          disabled={isSaving}
                          className="flex-1 border border-red-300 dark:border-red-900 contrast:border-red-800 rounded-lg text-red-600 dark:text-red-200 contrast:text-red-200 bg-red-50 dark:bg-red-800 contrast:bg-red-800 px-4 py-2 flex items-center justify-center hover:bg-red-100 dark:hover:bg-red-900 contrast:hover:bg-red-900 transition-colors"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        )}
      </div>
      <PoiModal
        open={isModalOpen}
        onClose={handleModalClose}
        mode={modalMode}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        onSubmit={handleSubmitPOI}
        isSubmitting={isSaving}
        onTranslationsGenerated={setTranslations}
        categories={categories}
        harborId={harborId}
      />
      <ConfirmationModal
        open={!!poiToDelete}
        onClose={() => setPoiToDelete(null)}
        title="Delete POI"
        message={`Are you sure you want to delete the POI "${poiToDelete?.name ?? ''}"? This action cannot be undone.`}
        onConfirm={() => poiToDelete && handleDeletePOI(poiToDelete.placeId)}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmButtonVariant="danger"
        disabled={isSaving}
      />
    </div>
  );
};

const BookingManagement = () => {
  const harborId = useSelector(
    (state: RootState) => state.auth.selectedHarborId
  );

  return (
    <BookingListings
      harborId={harborId || undefined}
      showExportControls={true}
    />
  );
};

const ServiceManagement = () => <HarborServiceListings />;

const ClubRequestsManagement = () => <HarborClubRequests />;

const ServiceBookingManagementTab = () => {
  const harborId = useSelector(
    (state: RootState) => state.auth.selectedHarborId
  );
  return <ServiceBookingManagement harborId={harborId || undefined} />;
};

const LockedModuleMessage = ({ moduleName }: { moduleName: string }) => {
  return (
    <div className="flex flex-col items-center justify-center p-8 md:p-12 bg-white dark:bg-black contrast:bg-black rounded-xl border border-gray-200 dark:border-coal-100 contrast:border-black shadow-md text-center max-w-xl mx-auto my-8">
      <div className="p-4 bg-amber-50 dark:bg-amber-950/30 contrast:bg-amber-950/50 rounded-full mb-4 text-amber-500 contrast:text-amber-200">
        <Lock className="w-10 h-10" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 dark:text-white contrast:text-white mb-2">
        {moduleName} is Locked
      </h3>
      <p className="text-gray-500 dark:text-gray-600 contrast:text-white/50 dark:text-white/50 text-sm leading-relaxed mb-6">
        Upgrade your plan to access these modules. Your existing data is safely
        preserved, but a higher tier plan is required to access the service
        management features.
      </p>
      <div className="px-4 py-2 bg-amber-100 dark:bg-amber-900/30 contrast:bg-amber-900/50 text-amber-800 dark:text-amber-200 contrast:text-amber-100 rounded-lg text-xs font-semibold border border-transparent contrast:border-amber-500">
        Locked State Active
      </div>
    </div>
  );
};

export default function HarborDetails() {
  const location = useLocation();
  const { layout } = useDemo1Layout() as unknown as Demo1LayoutContext;
  const { hasPermission } = usePermissions();
  const { hasCapability } = usePlanPermissions();
  const roles = useCurrentUserRoles();
  const isAdmin = roles.includes(UserRoles.ADMIN);

  const canManageServices = hasCapability(
    HarborCapability.SERVICES_MANAGE_SERVICES
  );
  const canManageServiceBookings = hasCapability(
    HarborCapability.SERVICES_MANAGE_BOOKINGS
  );
  const canManageServicesRequests = hasCapability(
    HarborCapability.SERVICES_MANAGE_REQUESTS
  );

  const navItems = useMemo(() => {
    const items = [...baseNavItems];

    const canManageTourismHighlights = hasCapability(
      HarborCapability.TOURISM_HIGHLIGHTS_MANAGE
    );
    const canAccessTourismHighlights = hasPermission('tourism_read');

    // Filter tabs based on plan permission for amenities and services
    // const canManageAmenitiesAndServices = hasCapability(
    //   HarborCapability.DISCOVERY_MANAGE_AMENITIES_AND_SERVICES
    // );

    // Filter tabs based on plan permission for bookings
    const canManageBookables = hasCapability(HarborCapability.BOOKINGS_ADDONS);

    const canManageBerths = hasCapability(
      HarborCapability.INVENTORY_MANAGE_BERTHS
    );

    const canManagePiers = hasCapability(
      HarborCapability.INVENTORY_MANAGE_PIER
    );

    const canManageCategories = hasCapability(
      HarborCapability.INVENTORY_MANAGE_CATEGORIES
    );

    // Check if user can accept online bookings or manage booking requests
    const canAcceptOnlineBookings = hasCapability(
      HarborCapability.BOOKINGS_ACCEPT_ONLINE_BOOKINGS
    );
    const canManageBookingRequests = hasCapability(
      HarborCapability.BOOKINGS_MANAGE_BOOKING_REQUESTS
    );
    const canAccessBookingManagement =
      canAcceptOnlineBookings || canManageBookingRequests;

    // Check if user can access finance reports
    const canAccessFinanceReports = hasCapability(
      HarborCapability.BOOKINGS_REPORTS_BASIC
    );

    const canAccessPiersManagement = hasPermission(
      'berth_pier_management_read'
    );
    const canAccessBerthManagement = hasPermission('berth_management_read');
    const canAccessBerthCategoryManagement = hasPermission(
      'berth_category_management_read'
    );
    const canAccessAddonManagement = hasPermission(
      'harbor_addon_management_read'
    );
    const canAccessReadBookingManagement = hasPermission(
      'berth_booking_management_read'
    );
    const canAccessServiceManagement = hasPermission('service_management_read');
    const canAccessServiceBookingManagement = hasPermission(
      'service_booking_read'
    );
    const canAccessClubRequests = hasPermission(
      'harbor_association_management_read'
    );

    const canAccessAvailabilityCalendar = hasPermission(
      'availibility_calender_read'
    );
    const canViewFinanceReports = hasPermission('finance_reporting_view');

    const canManageAvailabilityCalendar = hasCapability(
      HarborCapability.AVAILABILITY_MANAGE
    );

    const canManagePOIs = hasCapability(HarborCapability.POIS_MANAGE);

    const canManagePOIsRequests = hasCapability(
      HarborCapability.POIS_MANAGE_REQUESTS
    );

    const hasManageBasicProfileCapability = hasCapability(
      HarborCapability.DISCOVERY_MANAGE_BASIC_PROFILE
    );

    const canManageClubAssociation = hasCapability(
      HarborCapability.ASSOCIATIONS_MANAGE_CLUB_ASSOCIATIONS
    );

    // Filter service request management tab based on permission
    return items.filter(
      (item) =>
        isAdmin ||
        ((item.id !== 'tourism-highlights' ||
          (canAccessTourismHighlights && canManageTourismHighlights)) &&
          (item.id !== 'piers-management' ||
            (canAccessPiersManagement && canManagePiers)) &&
          // Booking-related tabs require BOOKINGS_MANAGE_BOOKABLES capability
          (item.id !== 'poi-management' ||
            (canManagePOIs && hasPermission('poi_management_read'))) &&
          (item.id !== 'availability-calendar' ||
            (canManageAvailabilityCalendar && canAccessAvailabilityCalendar)) &&
          (item.id !== 'berth-management' ||
            (canManageBerths && canAccessBerthManagement)) &&
          (item.id !== 'berth-category-management' ||
            (canManageCategories && canAccessBerthCategoryManagement)) &&
          (item.id !== 'addon-management' ||
            (canManageBookables && canAccessAddonManagement)) &&
          // Booking management tab requires BOOKINGS_ACCEPT_ONLINE_BOOKINGS or BOOKINGS_MANAGE_BOOKING_REQUESTS
          (item.id !== 'booking-management' ||
            (canAccessBookingManagement && canAccessReadBookingManagement)) &&
          // Finance reports tab requires BOOKINGS_REPORTS_BASIC capability
          (item.id !== 'finance-reports' ||
            (canViewFinanceReports &&
              canAccessFinanceReports &&
              canAccessReadBookingManagement)) &&
          // Service-related tabs display even if locked (just check read permission)
          (item.id !== 'service-management' || canAccessServiceManagement) &&
          (item.id !== 'service-booking-management' ||
            canAccessServiceBookingManagement) &&
          (item.id !== 'service-request-management' ||
            hasPermission('service_request_read')) &&
          (item.id !== 'poi-request-management' ||
            (canManagePOIsRequests && hasPermission('poi_request_read'))) &&
          (item.id !== 'club-requests' ||
            (canManageClubAssociation && canAccessClubRequests)) &&
          (item.id !== 'access-information' ||
            (hasPermission('harbor_management_update') &&
              hasManageBasicProfileCapability)))
    );
  }, [hasPermission, hasCapability, isAdmin]);

  const urlSearchParams = new URLSearchParams(location.search);
  const tabFromUrl = urlSearchParams.get('tab');

  const [activeTab, setActiveTab] = useState(
    tabFromUrl && navItems.some((item) => item.id === tabFromUrl)
      ? tabFromUrl
      : navItems[0].id
  );

  const harborId = useSelector(
    (state: RootState) => state.auth.selectedHarborId
  );
  const { data: harbor } = useGetHarborByIdQuery(harborId || '', {
    skip: !harborId,
  });

  // Update URL when tab changes
  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    if (currentParams.get('tab') !== activeTab) {
      currentParams.set('tab', activeTab);
      window.history.replaceState(
        null,
        '',
        `${location.pathname}?${currentParams.toString()}`
      );
    }
  }, [activeTab, location.pathname, location.search]);

  // Mobile pager: cycle through tabs one at a time
  const currentIndex = navItems.findIndex((item) => item.id === activeTab);
  const goPrevTab = () => {
    const prevIndex = (currentIndex - 1 + navItems.length) % navItems.length;
    const id = navItems[prevIndex].id;
    setActiveTab(id);
    window.history.pushState(null, '', `?tab=${id}`);
  };
  const goNextTab = () => {
    const nextIndex = (currentIndex + 1) % navItems.length;
    const id = navItems[nextIndex].id;
    setActiveTab(id);
    window.history.pushState(null, '', `?tab=${id}`);
  };

  const renderContent = () => {
    // Check plan permission for amenities and services

    // Check plan permission for bookings
    const canManageBookables = hasCapability(HarborCapability.BOOKINGS_ADDONS);
    const canAccessPiersManagement = hasPermission(
      'berth_pier_management_read'
    );
    const canAccessBerthManagement = hasPermission('berth_management_read');
    const canAccessAvailabilityCalendar = hasPermission(
      'availibility_calender_read'
    );

    // Check if user can accept online bookings or manage booking requests
    const canAcceptOnlineBookings = hasCapability(
      HarborCapability.BOOKINGS_ACCEPT_ONLINE_BOOKINGS
    );
    const canManageBookingRequests = hasCapability(
      HarborCapability.BOOKINGS_MANAGE_BOOKING_REQUESTS
    );
    const canAccessBookingManagement =
      canAcceptOnlineBookings || canManageBookingRequests;

    // Check if user can access finance reports
    const canAccessFinanceReports = hasCapability(
      HarborCapability.BOOKINGS_REPORTS_BASIC
    );

    const canManageBerths = hasCapability(
      HarborCapability.INVENTORY_MANAGE_BERTHS
    );

    const canManagePiers = hasCapability(
      HarborCapability.INVENTORY_MANAGE_PIER
    );

    const canManageCategories = hasCapability(
      HarborCapability.INVENTORY_MANAGE_CATEGORIES
    );

    const canManageAvailabilityCalendar = hasCapability(
      HarborCapability.AVAILABILITY_MANAGE
    );

    const canManagePOIs = hasCapability(HarborCapability.POIS_MANAGE);

    const canManagePOIsRequests = hasCapability(
      HarborCapability.POIS_MANAGE_REQUESTS
    );

    const canManageServices = hasCapability(
      HarborCapability.SERVICES_MANAGE_SERVICES
    );

    const canManageServiceBookings = hasCapability(
      HarborCapability.SERVICES_MANAGE_BOOKINGS
    );

    const canManageServicesRequests = hasCapability(
      HarborCapability.SERVICES_MANAGE_REQUESTS
    );

    const canManageClubAssociation = hasCapability(
      HarborCapability.ASSOCIATIONS_MANAGE_CLUB_ASSOCIATIONS
    );

    const canAccessClubRequests = hasPermission(
      'harbor_association_management_read'
    );

    switch (activeTab) {
      case 'harbor-management':
        return harbor ? <HarborManagement harbor={harbor} /> : null;
      case 'access-information':
        if (!isAdmin && !canAccessBookingManagement) {
          return (
            <div className="p-4 text-center text-gray-500">
              You don't have permission to access Access Information.
            </div>
          );
        }
        return harbor ? <HarborAccessInformation harbor={harbor} /> : null;
      case 'berth-management':
        if (!isAdmin && (!canManageBerths || !canAccessBerthManagement)) {
          return (
            <div className="p-4 text-center text-gray-500">
              You don't have permission to access Berth Management.
            </div>
          );
        }
        return <BerthManagement />;
      case 'berth-category-management':
        if (!isAdmin && !canManageCategories) {
          return (
            <div className="p-4 text-center text-gray-500">
              You don't have permission to access Berth Category Management.
            </div>
          );
        }
        return <BerthCategoryManagement />;
      case 'addon-management':
        if (!isAdmin && !canManageBookables) {
          return (
            <div className="p-4 text-center text-gray-500">
              You don't have permission to access Addon Management.
            </div>
          );
        }
        return <AddonManagement />;
      case 'poi-management':
        if (
          !isAdmin &&
          (!canManagePOIs || !hasPermission('poi_management_read'))
        ) {
          return (
            <div className="p-4 text-center text-gray-500">
              You don't have permission to access POI Management.
            </div>
          );
        }
        return harbor ? <POIManagement harbor={harbor} /> : null;
      case 'tourism-highlights':
        if (!isAdmin && !hasPermission('tourism_read')) {
          return (
            <div className="p-4 text-center text-gray-500">
              You don't have permission to access Tourism Highlights.
            </div>
          );
        }
        return <TourismHighlightsTab />;
      case 'service-management':
        if (!isAdmin && !hasPermission('service_management_read')) {
          return (
            <div className="p-4 text-center text-gray-500">
              You don't have permission to access Service Management.
            </div>
          );
        }
        if (!isAdmin && !canManageServices) {
          return <LockedModuleMessage moduleName="Service Management" />;
        }
        return <ServiceManagement />;
      case 'booking-management':
        if (!isAdmin && !canAccessBookingManagement) {
          return (
            <div className="p-4 text-center text-gray-500">
              You don't have permission to access Booking Management.
            </div>
          );
        }
        return <BookingManagement />;
      case 'service-booking-management':
        if (!isAdmin && !hasPermission('service_booking_read')) {
          return (
            <div className="p-4 text-center text-gray-500">
              You don't have permission to access Service Booking Management.
            </div>
          );
        }
        if (!isAdmin && !canManageServiceBookings) {
          return (
            <LockedModuleMessage moduleName="Service Booking Management" />
          );
        }
        return <ServiceBookingManagementTab />;
      case 'piers-management':
        if (!isAdmin && !canAccessPiersManagement && !canManagePiers) {
          return (
            <div className="p-4 text-center text-gray-500">
              You don't have permission to access Piers Management.
            </div>
          );
        }
        return <PierListings />;
      case 'availability-calendar':
        if (
          !isAdmin &&
          (!canManageAvailabilityCalendar || !canAccessAvailabilityCalendar)
        ) {
          return (
            <div className="p-4 text-center text-gray-500">
              You don't have permission to access Availability Calendar.
            </div>
          );
        }
        return <AvailabilityCalendar />;
      case 'club-requests':
        if (!isAdmin && (!canManageClubAssociation || !canAccessClubRequests)) {
          return (
            <div className="p-4 text-center text-gray-500">
              You don't have permission to access Club Requests.
            </div>
          );
        }
        return <ClubRequestsManagement />;
      case 'service-request-management':
        if (!isAdmin && !hasPermission('service_request_read')) {
          return (
            <div className="p-4 text-center text-gray-500">
              You don't have permission to access Service Request Management.
            </div>
          );
        }
        if (!isAdmin && !canManageServicesRequests) {
          return (
            <LockedModuleMessage moduleName="Service Request Management" />
          );
        }
        return <ServiceRequestManagement harborId={harborId} />;
      case 'poi-request-management':
        if (
          !isAdmin &&
          (!canManagePOIsRequests || !hasPermission('poi_request_read'))
        ) {
          return (
            <div className="p-4 text-center text-gray-500">
              You don't have permission to access POI Request Management.
            </div>
          );
        }
        return <PoiRequestManagement harborId={harborId} />;
      case 'finance-reports':
        if (!isAdmin && !canAccessFinanceReports) {
          return (
            <div className="p-4 text-center text-gray-500">
              You don't have permission to access Finance Reports.
            </div>
          );
        }
        return <HarborFinanceReports harborId={harborId || undefined} />;
      default:
        return harbor ? <HarborManagement harbor={harbor} /> : null;
    }
  };

  return (
    <div className="harbor-page w-full">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4">
          <div className="mt-3">
            <Link
              to="/habor-management"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
              aria-label="Back to harbor management"
            >
              <KeenIcon
                icon="black-left"
                className="text-xl"
                aria-hidden="true"
              />
            </Link>
          </div>
          <header
            className={`md:border-b border-gray-200 harbor-header ${layout.options.sidebar.collapse ? 'header-collapsed' : ''}`}
          >
            <nav
              className="-mb-px flex space-x-6 overflow-x-auto px-1"
              aria-label="Tabs"
              role="tablist"
              style={{ maxWidth: 'calc(100vw - 200px)' }}
            >
              {navItems.map((item) => {
                const isLocked =
                  !isAdmin &&
                  ((item.id === 'service-management' && !canManageServices) ||
                    (item.id === 'service-booking-management' &&
                      !canManageServiceBookings) ||
                    (item.id === 'service-request-management' &&
                      !canManageServicesRequests));
                return (
                  <Link
                    key={item.name}
                    to={`?tab=${item.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab(item.id);
                      // Update URL without navigation
                      window.history.pushState(null, '', `?tab=${item.id}`);
                    }}
                    role="tab"
                    aria-selected={activeTab === item.id}
                    aria-controls={`harbor-tabpanel-${item.id}`}
                    className={`whitespace-nowrap py-4 px-3 border-b-2 font-medium text-sm flex items-center gap-1.5 flex-shrink-0 min-w-fit contrast:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-sm ${activeTab === item.id
                      ? 'border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400 contrast:text-blue-500 contrast:border-blue-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-white/70 dark:hover:text-white dark:hover:border-white/50'
                      }`}
                  >
                    {isLocked && <Lock className="w-3.5 h-3.5 text-gray-400" />}
                    {item.name}
                  </Link>
                );
              })}
            </nav>
            {/* Mobile-only pager: hidden by default, shown at 320px via CSS */}
            <div
              className="harbor-mobile-nav hidden items-center justify-between"
              aria-label="Tabs mobile"
            >
              <button
                type="button"
                className="nav-arrow prev"
                onClick={goPrevTab}
                aria-label="Previous section"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div
                className="current-tab flex items-center justify-center gap-1.5"
                aria-current="page"
              >
                {(() => {
                  const currentItem = navItems[currentIndex];
                  const isCurrentLocked =
                    currentItem &&
                    !isAdmin &&
                    ((currentItem.id === 'service-management' &&
                      !canManageServices) ||
                      (currentItem.id === 'service-booking-management' &&
                        !canManageServiceBookings) ||
                      (currentItem.id === 'service-request-management' &&
                        !canManageServicesRequests));
                  return (
                    <>
                      {isCurrentLocked && (
                        <Lock className="w-4 h-4 text-gray-400" />
                      )}
                      {currentItem?.name}
                    </>
                  );
                })()}
              </div>
              <button
                type="button"
                className="nav-arrow next"
                onClick={goNextTab}
                aria-label="Next section"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </header>
        </div>
        <div
          className="py-8"
          role="tabpanel"
          id={`harbor-tabpanel-${activeTab}`}
          aria-label={`${navItems[currentIndex]?.name || 'Section'} content`}
        >
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
